<template>
  <div
    ref="$el"
    :class="cmpClass"
    :style="cmpStyle"
    @click="e => $emit('click', e)"
    @mousedown="e => handleMousedown(e)"
    @mouseenter="e => handleMouseenter(e)"
    @mouseleave="e => handleMouseleave(e)"
    @mouseup="e => handleMouseup(e)"
  >
    <slot />
  </div>
</template>
<script setup lang="ts">
  import {
    ref, reactive, watch, watchEffect, computed, onMounted, withDefaults,
  } from 'vue'
  import { useHorizontalCollapsible, useVerticalCollapsible } from '../../composables'
  import { MouseState, CollapseDirection } from './interface'

  const props = withDefaults(
    defineProps<{
      /**
       * 设置 `grid-row`:
       * - 如果为 `string` 则直接设置为对应样式 https://developer.mozilla.org/en-US/docs/Web/CSS/grid-row
       * - 如果为 `number` 则设置为 `span ${row}`，使 `GridItem` 跨对应行数
       */
      row?: string | number
      /**
       * 设置 `grid-column`:
       * - 如果为 `string` 则直接设置为对应样式 https://developer.mozilla.org/en-US/docs/Web/CSS/grid-column
       * - 如果为 `number` 则设置为 `span ${row}`，使 `GridItem` 跨对应列数
       */
      column?: string | number
      /**
       * 是否横向、纵向折叠
       */
      useCollapse?: boolean
      /**
       * 是否横向、纵向折叠
       */
      collapse?: CollapseDirection
    }>(),
    {
      row: 1,
      column: 1,
      useCollapse: false,
      collapse: () => ({}),
    },
  )

  const emits = defineEmits<{(e: 'click', event: MouseEvent): void; (e: 'mousedown', event: MouseEvent): void; (e: 'mouseenter', event: MouseEvent): void; (e: 'mouseleave', event: MouseEvent): void; (e: 'mouseup', event: MouseEvent): void; (e: 'state-change', state: MouseState): void; (e: 'horizontal-collapsed'): void; (e: 'horizontal-expanded'): void; (e: 'vertical-collapsed'): void; (e: 'vertical-expanded'): void}>()

  const state = reactive<MouseState>({
    pressing: false,
    hover: false,
  })

  watch(
    state,
    v => emits('state-change', v),
  )

  const $el = ref()

  const cmpClass = computed(
    () => ({
      'd-grid-item': true,
      pressing: state.pressing,
    }),
  )

  const cmpStyle = computed(
    () => ({
      gridRow: typeof props.row === 'number' ? `span ${props.row}` : props.row,
      gridColumn: typeof props.column === 'number' ? `span ${props.column}` : props.column,
      // in order to control the collapsing behavior
      overflow: props.useCollapse && 'hidden',
    }),
  )

  function handleMousedown(e: MouseEvent) {
    emits('mousedown', e)
    state.pressing = true
  }

  function handleMouseenter(e: MouseEvent) {
    emits('mouseenter', e)
    state.hover = true
  }

  function handleMouseleave(e: MouseEvent) {
    emits('mouseleave', e)
    state.pressing = false
    state.hover = false
  }

  function handleMouseup(e: MouseEvent) {
    emits('mouseup', e)
    state.pressing = false
  }

  onMounted(
    () => {
      if (props.useCollapse) {
        const horizontal = useHorizontalCollapsible(
          $el.value,
          {
            callback: {
              collapsed: () => emits('horizontal-collapsed'),
              expanded: () => emits('horizontal-expanded'),
            },
          },
        )
        const vertical = useVerticalCollapsible(
          $el.value,
          {
            initCollapsed: props.collapse.y,
            callback: {
              collapsed: () => emits('vertical-collapsed'),
              expanded: () => emits('vertical-expanded'),
            },
          },
        )

        watch(
          () => props.collapse,
          (v, ov) => {
            if (v.x !== ov?.x) {
              horizontal(v.x)
            }
            if (v.y !== ov?.y) {
              vertical(v.y)
            }
          },
          { immediate: true },
        )
      }
    },
  )

  const exposed = reactive({
    $el: $el.value,
  })

  watchEffect(
    () => {
      exposed.$el = $el.value
    },
  )

  defineExpose(exposed)
</script>
